/**
 * CommercialQuoteEngine — Iter-156 · Phase 0 rebuild.
 * Now posts to /api/quote-wizard/submit (single canonical commercial
 * quote engine). Vertical-specific inputs are shown conditionally
 * based on the selected business type:
 *   Bank    → vault_handling + secure_area_count
 *   Stadium → events_per_month + event_duration_hours
 *   Airport → tsa_security_zones
 */
import { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2 } from 'lucide-react';
import { INDUSTRIES } from '../lib/industryData';

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#C9A962';
const inputCls = 'w-full rounded-md px-3 py-2.5 text-sm text-white bg-[#141414] border-2 border-[#3f3f46] focus:border-[#C9A962] focus:outline-none';
const labelCls = 'block text-[11px] font-bold uppercase tracking-widest mb-1.5 text-[#C9A962]';
const groupCls = 'text-xs font-black uppercase tracking-[0.25em] mb-3 mt-2 text-white';

const ADD_ONS = ['Window Cleaning', 'Pressure Washing', 'Dumpster-Pad Cleaning', 'Bulk Removal',
  'Carpet Extraction', 'Day Porter', 'Deep Cleaning', 'Event Cleanup', 'Emergency Response'];
const TIER_LABELS = { basic: 'ESSENTIAL CARE', standard: 'PROFESSIONAL CARE', premium: 'EXECUTIVE CARE' };

// Iter-169 · Phase 4 — freshness badge for auto-detected prefill.
// Returns a compact human-friendly string:
//   • "Detected 3 days ago"
//   • "Detected today"
//   • "From inspection on Sep 8, 2025"
//   • "From last inspection"  (fallback when no timestamp)
function _formatInspectionHint(detectedAtISO) {
  if (!detectedAtISO) return 'From last inspection · edit if needed';
  const dt = new Date(detectedAtISO);
  if (isNaN(dt.getTime())) return 'From last inspection · edit if needed';
  const now = new Date();
  const diffMs = now.getTime() - dt.getTime();
  const days = Math.floor(diffMs / 86400000);
  if (days <= 0) return 'Detected today · edit if needed';
  if (days === 1) return 'Detected 1 day ago · edit if needed';
  if (days < 30) return `Detected ${days} days ago · edit if needed`;
  const label = dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  return `From inspection on ${label} · edit if needed`;
}

const VAULT_OPTIONS = [
  'None — vault serviced by client staff',
  'Perimeter cleaning only (no entry)',
  'Escorted access — client-authorized procedure',
  'Standard restricted area (no NECS access)',
];

export default function CommercialQuoteEngine({ initialType = 'office', prefill = {}, onBack }) {
  const [form, setForm] = useState({
    business_type: prefill.business_type || initialType,
    property_name: prefill.property_name || '',
    address: prefill.address || '',
    square_footage: prefill.square_footage || '',
    buildings: prefill.buildings || '1',
    floors: prefill.floors || '1',
    operating_hours: prefill.operating_hours || '',
    occupants: prefill.occupants || '',
    daily_traffic: prefill.daily_traffic || 'medium',
    cleaning_frequency: '3x_week',
    dumpster_count: '', dumpster_size: 'medium', waste_volume: '', waste_visits_per_week: '',
    recycling: false, bulk_items: false,
    restroom_count: '', fixtures_per_restroom: '3', restroom_frequency: '',
    hard_floor_sqft: '', carpet_sqft: '', floor_type: 'tile', floor_care_need: 'none',
    additional_services: [],
    tier: prefill.tier || 'standard',
    contract_months: 12,
    vault_handling: '',
    secure_area_count: '',
    events_per_month: '',
    event_duration_hours: '',
    tsa_security_zones: '',
    full_name: prefill.full_name || '',
    company_name: prefill.company_name || '',
    email: prefill.email || '',
    phone: prefill.phone || '',
    preferred_starting_date: '',
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleAddon = (s) => setForm((f) => ({
    ...f,
    additional_services: f.additional_services.includes(s)
      ? f.additional_services.filter((x) => x !== s)
      : [...f.additional_services, s],
  }));

  const submit = async () => {
    setError(''); setBusy(true);
    try {
      // Build the quote-wizard payload. Preserve full facility detail
      // via extra fields (backend model has extra=allow so the assessor
      // sees every input).
      const payload = {
        property_type: form.business_type,
        facility_name: form.property_name,
        contact_email: form.email,
        square_footage: parseFloat(form.square_footage) || 0,
        tier: form.tier,
        contract_months: parseInt(form.contract_months, 10) || 12,
        // extras (survive because model_config.extra = "allow")
        address: form.address,
        buildings: parseInt(form.buildings, 10) || 1,
        floors: parseInt(form.floors, 10) || 1,
        operating_hours: form.operating_hours,
        occupants: parseInt(form.occupants, 10) || 0,
        daily_traffic: form.daily_traffic,
        cleaning_frequency: form.cleaning_frequency,
        dumpster_count: parseInt(form.dumpster_count, 10) || 0,
        dumpster_size: form.dumpster_size,
        waste_volume: form.waste_volume,
        waste_visits_per_week: parseInt(form.waste_visits_per_week, 10) || 0,
        recycling: form.recycling,
        bulk_items: form.bulk_items,
        restroom_count: parseInt(form.restroom_count, 10) || 0,
        fixtures_per_restroom: parseInt(form.fixtures_per_restroom, 10) || 0,
        restroom_frequency: form.restroom_frequency,
        hard_floor_sqft: parseInt(form.hard_floor_sqft, 10) || 0,
        carpet_sqft: parseInt(form.carpet_sqft, 10) || 0,
        floor_type: form.floor_type,
        floor_care_need: form.floor_care_need,
        additional_services: form.additional_services,
        full_name: form.full_name,
        company_name: form.company_name,
        phone: form.phone,
        preferred_starting_date: form.preferred_starting_date,
      };
      // Vertical-specific fields (only include when relevant)
      if (form.business_type === 'bank') {
        if (form.vault_handling) payload.vault_handling = form.vault_handling;
        if (form.secure_area_count) payload.secure_area_count = parseInt(form.secure_area_count, 10) || 0;
      }
      if (form.business_type === 'stadium') {
        if (form.events_per_month) payload.events_per_month = parseInt(form.events_per_month, 10) || 0;
        if (form.event_duration_hours) payload.event_duration_hours = parseFloat(form.event_duration_hours) || 0;
      }
      if (form.business_type === 'airport') {
        if (form.tsa_security_zones) payload.tsa_security_zones = parseInt(form.tsa_security_zones, 10) || 0;
      }

      const res = await fetch(`${API}/api/quote-wizard/submit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.detail?.[0]?.msg || d?.detail || 'Submission failed');
      setResult(d);
    } catch (e) { setError(String(e.message)); }
    setBusy(false);
  };

  if (result) {
    const est = result.estimate || {};
    const isBank = form.business_type === 'bank';
    const isStadium = form.business_type === 'stadium';
    const isAirport = form.business_type === 'airport';
    return (
      <Card className="p-8 text-center" style={{ backgroundColor: '#1f1f1f', border: `2px solid ${GOLD}` }} data-testid="cq-result">
        <CheckCircle2 className="h-10 w-10 mx-auto mb-3" style={{ color: GOLD }} />
        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Estimated Monthly Investment</p>
        <p className="text-4xl font-black text-white mt-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="cq-final-range">
          ${Number(est.monthly_low || 0).toLocaleString()} – ${Number(est.monthly_high || 0).toLocaleString()}
          <span className="text-base font-semibold ml-2 text-zinc-500">/ month</span>
        </p>
        <p className="text-xs mt-2" style={{ color: GOLD }} data-testid="cq-recommended-tier">
          Recommended: {TIER_LABELS[result.tier] || TIER_LABELS.standard}
        </p>
        {Array.isArray(est.components) && est.components.length > 0 && (
          <div className="mt-4 rounded-lg p-4 text-sm text-left mx-auto max-w-md" style={{ backgroundColor: '#141414', border: '1px solid #3f3f46' }} data-testid="cq-line-items">
            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2 text-center">Line Items</p>
            {est.components.map((c, i) => {
              const isVault = /^Vault handling/i.test(c.label);
              const isSecure = /^Secure area sweeps/i.test(c.label);
              const isEvent = /^Event service/i.test(c.label);
              const isTsa = /^TSA security zones/i.test(c.label);
              return (
                <div key={i} className="flex justify-between py-1 border-b border-[#2a2a2a] last:border-0" data-testid={
                  isVault ? 'cq-line-vault' :
                  isSecure ? 'cq-line-secure-area' :
                  isEvent ? 'cq-line-event' :
                  isTsa ? 'cq-line-tsa' :
                  `cq-line-${i}`
                }>
                  <span className="text-white">{c.label}</span>
                  <span style={{ color: GOLD }}>${Number(c.amount).toLocaleString()}</span>
                </div>
              );
            })}
          </div>
        )}
        <p className="text-xs italic text-zinc-500 mt-3" data-testid="cq-disclaimer">
          Final pricing is subject to facility assessment, scope validation and final proposal.
        </p>
        <p className="mt-4 text-sm font-bold" style={{ color: GOLD }} data-testid="cq-quote-id">
          Quote ID: {result.quote_id}
        </p>
        {(isBank || isStadium || isAirport) && (
          <p className="mt-2 text-xs uppercase tracking-widest" style={{ color: '#7CFC8A' }} data-testid="cq-strategic-enterprise-badge">
            Strategic Enterprise · Facility Assessment Required
          </p>
        )}
      </Card>
    );
  }

  const isBank = form.business_type === 'bank';
  const isStadium = form.business_type === 'stadium';
  const isAirport = form.business_type === 'airport';

  return (
    <Card className="p-6 sm:p-8" style={{ backgroundColor: '#1f1f1f', border: '2px solid #3f3f46' }} data-testid="commercial-quote-engine">
      {onBack && (
        <button onClick={onBack}
                className="mb-4 text-[10px] uppercase tracking-widest text-zinc-300 hover:text-white"
                data-testid="cq-back">
          ← Back to Recommended Services
        </button>
      )}
      <p className={groupCls} style={{ color: GOLD }}>Property Information</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className={labelCls}>Business Type *</label>
          <select className={inputCls} value={form.business_type} onChange={set('business_type')} data-testid="cq-business-type">
            {Object.entries(INDUSTRIES).map(([k, v]) => {
              const optLabel = `${v.necs} — ${v.label}`;
              return <option key={k} value={k} label={optLabel}>{optLabel}</option>;
            })}
          </select>
        </div>
        <div><label className={labelCls}>Property Name *</label><input className={inputCls} value={form.property_name} onChange={set('property_name')} data-testid="cq-property-name" /></div>
        <div><label className={labelCls}>Address *</label><input className={inputCls} value={form.address} onChange={set('address')} data-testid="cq-address" /></div>
        <div><label className={labelCls}>Square Footage *</label><input type="number" min="200" className={inputCls} value={form.square_footage} onChange={set('square_footage')} data-testid="cq-sqft" /></div>
        <div><label className={labelCls}>Buildings</label><input type="number" min="1" className={inputCls} value={form.buildings} onChange={set('buildings')} data-testid="cq-buildings" /></div>
        <div><label className={labelCls}>Floors</label><input type="number" min="1" className={inputCls} value={form.floors} onChange={set('floors')} data-testid="cq-floors" /></div>
        <div>
          <label className={labelCls}>Operating Hours</label>
          <input className={inputCls} placeholder="e.g. 8am–6pm M–F"
                 value={form.operating_hours} onChange={set('operating_hours')}
                 data-testid="cq-hours" />
          {prefill.inspection_prefill_source && form.operating_hours && (
            <p className="text-[10px] mt-1 italic font-medium" style={{ color: '#10b981' }}
               data-testid="cq-hours-inspection-hint">
              {_formatInspectionHint(prefill.inspection_prefill_detected_at)}
            </p>
          )}
        </div>
        <div>
          <label className={labelCls}>Employees / Occupants</label>
          <input type="number" min="0" className={inputCls}
                 value={form.occupants} onChange={set('occupants')}
                 data-testid="cq-occupants" />
          {prefill.inspection_prefill_source && form.occupants && (
            <p className="text-[10px] mt-1 italic font-medium" style={{ color: '#10b981' }}
               data-testid="cq-occupants-inspection-hint">
              {_formatInspectionHint(prefill.inspection_prefill_detected_at)}
            </p>
          )}
        </div>
        <div>
          <label className={labelCls}>Approx. Daily Traffic</label>
          <select className={inputCls} value={form.daily_traffic} onChange={set('daily_traffic')} data-testid="cq-traffic">
            <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
          </select>
        </div>
      </div>

      {/* Iter-156 · Phase 0 — Bank vertical fields */}
      {isBank && (
        <>
          <p className={groupCls} style={{ color: '#7CFC8A' }}>Bank · Strategic Enterprise</p>
          <div className="grid sm:grid-cols-2 gap-4" data-testid="cq-bank-fields">
            <div>
              <label className={labelCls}>Vault Handling</label>
              <select className={inputCls} value={form.vault_handling} onChange={set('vault_handling')} data-testid="cq-vault-handling">
                <option value="">Select an option</option>
                {VAULT_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Secure Area Count</label>
              <input type="number" min="0" className={inputCls} value={form.secure_area_count} onChange={set('secure_area_count')} placeholder="e.g. 3" data-testid="cq-secure-area-count" />
            </div>
          </div>
        </>
      )}

      {/* Iter-156 · Phase 0 — Stadium vertical fields */}
      {isStadium && (
        <>
          <p className={groupCls} style={{ color: '#7CFC8A' }}>Stadium · Strategic Enterprise</p>
          <div className="grid sm:grid-cols-2 gap-4" data-testid="cq-stadium-fields">
            <div>
              <label className={labelCls}>Events per Month</label>
              <input type="number" min="0" className={inputCls} value={form.events_per_month} onChange={set('events_per_month')} placeholder="e.g. 8" data-testid="cq-events-per-month" />
            </div>
            <div>
              <label className={labelCls}>Event Duration (hours)</label>
              <input type="number" step="0.5" min="0" className={inputCls} value={form.event_duration_hours} onChange={set('event_duration_hours')} placeholder="e.g. 4" data-testid="cq-event-duration-hours" />
            </div>
          </div>
        </>
      )}

      {/* Iter-156 · Phase 0 — Airport vertical fields */}
      {isAirport && (
        <>
          <p className={groupCls} style={{ color: '#7CFC8A' }}>Airport · Strategic Enterprise</p>
          <div className="grid sm:grid-cols-2 gap-4" data-testid="cq-airport-fields">
            <div>
              <label className={labelCls}>TSA Security Zones</label>
              <input type="number" min="0" className={inputCls} value={form.tsa_security_zones} onChange={set('tsa_security_zones')} placeholder="e.g. 5" data-testid="cq-tsa-security-zones" />
            </div>
          </div>
        </>
      )}

      <p className={groupCls} style={{ color: GOLD }}>Service Program</p>
      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <label className={labelCls}>Cleaning Frequency *</label>
          <select className={inputCls} value={form.cleaning_frequency} onChange={set('cleaning_frequency')} data-testid="cq-frequency">
            <option value="daily">Daily</option>
            <option value="3x_week">3× weekly</option>
            <option value="5x_week">5× weekly</option>
            <option value="7x_week">7× weekly</option>
            <option value="multiple_daily">Multiple times per day</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Service Tier *</label>
          <select className={inputCls} value={form.tier} onChange={set('tier')} data-testid="cq-tier">
            <option value="basic">Essential Care</option>
            <option value="standard">Professional Care</option>
            <option value="premium">Executive Care</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Contract Term *</label>
          <select className={inputCls} value={form.contract_months} onChange={set('contract_months')} data-testid="cq-contract-months">
            <option value="12">12 Months</option>
            <option value="24">24 Months</option>
            <option value="36">36 Months</option>
          </select>
        </div>
      </div>

      <p className={groupCls} style={{ color: GOLD }}>Waste</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div><label className={labelCls}>Dumpsters</label><input type="number" min="0" className={inputCls} value={form.dumpster_count} onChange={set('dumpster_count')} data-testid="cq-dumpsters" /></div>
        <div>
          <label className={labelCls}>Dumpster Size</label>
          <select className={inputCls} value={form.dumpster_size} onChange={set('dumpster_size')} data-testid="cq-dumpster-size">
            <option value="small">Small</option><option value="medium">Medium</option><option value="large">Large</option>
          </select>
        </div>
        <div><label className={labelCls}>Waste Volume</label><input className={inputCls} placeholder="e.g. moderate" value={form.waste_volume} onChange={set('waste_volume')} data-testid="cq-waste-volume" /></div>
        <div><label className={labelCls}>Collection Visits / Week</label><input type="number" min="0" className={inputCls} value={form.waste_visits_per_week} onChange={set('waste_visits_per_week')} data-testid="cq-waste-visits" /></div>
      </div>
      <div className="flex flex-wrap gap-3 mt-3">
        {[['recycling', 'Recycling Requirements'], ['bulk_items', 'Bulk-Item Requirements']].map(([k, l]) => (
          <label key={k} className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider cursor-pointer border-2 ${form[k] ? 'bg-[#C9A962] text-black border-[#C9A962]' : 'text-[#C9A962] border-[#C9A962]/50'}`} data-testid={`cq-toggle-${k}`}>
            <input type="checkbox" className="hidden" checked={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.checked }))} />
            {form[k] && <CheckCircle2 className="h-3.5 w-3.5" />}{l}
          </label>
        ))}
      </div>

      <p className={groupCls} style={{ color: GOLD }}>Restrooms</p>
      <div className="grid sm:grid-cols-3 gap-4">
        <div><label className={labelCls}>Number of Restrooms</label><input type="number" min="0" className={inputCls} value={form.restroom_count} onChange={set('restroom_count')} data-testid="cq-restrooms" /></div>
        <div><label className={labelCls}>Fixtures per Restroom</label><input type="number" min="0" className={inputCls} value={form.fixtures_per_restroom} onChange={set('fixtures_per_restroom')} data-testid="cq-fixtures" /></div>
        <div><label className={labelCls}>Restroom Cleaning Frequency</label><input className={inputCls} placeholder="e.g. daily" value={form.restroom_frequency} onChange={set('restroom_frequency')} data-testid="cq-restroom-freq" /></div>
      </div>

      <p className={groupCls} style={{ color: GOLD }}>Floors</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div><label className={labelCls}>Hard Floor (sq ft)</label><input type="number" min="0" className={inputCls} value={form.hard_floor_sqft} onChange={set('hard_floor_sqft')} data-testid="cq-hard-floor" /></div>
        <div><label className={labelCls}>Carpet (sq ft)</label><input type="number" min="0" className={inputCls} value={form.carpet_sqft} onChange={set('carpet_sqft')} data-testid="cq-carpet" /></div>
        <div>
          <label className={labelCls}>Floor Type</label>
          <select className={inputCls} value={form.floor_type} onChange={set('floor_type')} data-testid="cq-floor-type">
            <option value="tile">Tile</option><option value="vct">VCT</option>
            <option value="concrete">Concrete</option><option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Do you need floor care?</label>
          <select className={inputCls} value={form.floor_care_need} onChange={set('floor_care_need')} data-testid="cq-floor-care">
            <option value="none">No</option>
            <option value="maintenance">Maintenance</option>
            <option value="polishing">Polishing &amp; Burnishing</option>
            <option value="strip_wax">Strip &amp; Wax</option>
            <option value="restoration">Restoration</option>
          </select>
        </div>
      </div>

      <p className={groupCls} style={{ color: GOLD }}>Additional Services</p>
      <div className="flex flex-wrap gap-3">
        {ADD_ONS.map((s) => (
          <label key={s} className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider cursor-pointer border-2 ${form.additional_services.includes(s) ? 'bg-[#C9A962] text-black border-[#C9A962]' : 'text-[#C9A962] border-[#C9A962]/50'}`} data-testid={`cq-addon-${s.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
            <input type="checkbox" className="hidden" checked={form.additional_services.includes(s)} onChange={() => toggleAddon(s)} />
            {form.additional_services.includes(s) && <CheckCircle2 className="h-3.5 w-3.5" />}{s}
          </label>
        ))}
      </div>

      <p className={groupCls} style={{ color: GOLD }}>Contact Details</p>
      <div className="grid sm:grid-cols-2 gap-4" data-testid="cq-contact-block">
        <input className={inputCls} placeholder="Full Name *" value={form.full_name} onChange={set('full_name')} data-testid="cq-name" />
        <input className={inputCls} placeholder="Company *" value={form.company_name} onChange={set('company_name')} data-testid="cq-company" />
        <input className={inputCls} type="email" placeholder="Email *" value={form.email} onChange={set('email')} data-testid="cq-email" />
        <input className={inputCls} placeholder="Phone *" value={form.phone} onChange={set('phone')} data-testid="cq-phone" />
        <div className="sm:col-span-2">
          <label className="block text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Preferred Starting Date *</label>
          <input type="date" className={inputCls} value={form.preferred_starting_date}
                 onChange={set('preferred_starting_date')} required
                 data-testid="cq-preferred-start-date" />
        </div>
      </div>
      <p className="text-xs italic text-zinc-500 mt-3">Your estimate is calculated and displayed after you submit your details.</p>

      {error && <p className="mt-3 text-sm text-red-400" data-testid="cq-error">{error}</p>}
      <Button onClick={submit}
        disabled={busy || !form.square_footage || !form.property_name || !form.address || !form.full_name || !form.company_name || !form.email || !form.phone || !form.preferred_starting_date}
        className="w-full mt-5 font-black uppercase tracking-widest py-6 text-base"
        style={{ backgroundColor: GOLD, color: '#1a1a1a', fontFamily: 'Barlow Condensed, sans-serif' }}
        data-testid="cq-submit-btn">
        {busy ? 'CALCULATING…' : 'GET MY ESTIMATE'}
      </Button>
    </Card>
  );
}
